import * as APIModel from '@/api/models';
import { IDropdownOption } from '@/common/EditableDropdown';

export interface IStaticDropdownData<T> {
  map: Record<string, T>;
  options: IDropdownOption[];
}

// Using `unknown` and casting inside for simplicity - all entities must have `idKey` and `labelKey` properties
const buildStaticDropdown = <T extends unknown>(entities: T[], idKey: keyof T, labelKey: keyof T): IStaticDropdownData<T> => {
  const map: Record<string, T> = {};
  const options: IDropdownOption[] = [];

  entities.forEach((item) => {
    // cast to string for ID, string for label
    const id = item[idKey] as string;
    const label = item[labelKey] as string;

    map[id] = item;
    options.push({ id, label });
  });

  return { map, options };
};

interface IBuildStaticDropdownOptionMapProps {
  ironFamily: APIModel.Family[];
  ironClub: APIModel.IronClub[];
  gripSize: APIModel.GripSize[];
}

export const buildStaticDropdownOptionMap = ({ ironFamily, ironClub, gripSize }: IBuildStaticDropdownOptionMapProps) => {
  return {
    ironFamilyDropdownOptionMap: buildStaticDropdown(ironFamily, 'familyId', 'name'),
    ironClubDropdownOptionMap: buildStaticDropdown(ironClub, 'ironClubId', 'name'),
    gripSizeDropdownOptionMap: buildStaticDropdown(gripSize, 'gripSizeId', 'name'),
  };
};

interface IFilterParams {
  familyId?: string | number;
}

// filter options byt any number of entity id
const matchesFilters = <T>(entity: T, filterParams: IFilterParams) => {
  return Object.entries(filterParams).every(([field, selectedId]) => {
    const allowedIds = entity[field as keyof T]; // ie: putterLevelIds: [1,2]

    // fail if the entity selectedId is not within allowedIds
    if (Array.isArray(allowedIds)) return allowedIds.includes(selectedId);
    else {
      // not an id array perform exact match
      return allowedIds === selectedId;
    }
  });
};

export interface IDynamicDropdownData<T> {
  map: Record<string, T>;
  // returns options filtered by multiple fields - filterParams: { allowedIdArray: selectedId }
  // eslint-disable-next-line no-unused-vars
  getOptions: (filterParams?: IFilterParams) => IDropdownOption[];
}

const buildDynamicDropdown = <T>(entities: T[], idKey: keyof T, labelKey: keyof T): IDynamicDropdownData<T> => {
  const map: Record<string, T> = {};
  const options: IDropdownOption[] = [];

  entities.forEach((item) => {
    const id = item[idKey] as string;
    const label = item[labelKey] as string;

    map[id] = item;
    options.push({ id, label });
  });

  return {
    map,
    getOptions: (filterParams?: IFilterParams) => {
      if (!filterParams) return [...options]; // return everything
      return options.filter((opt) => matchesFilters(map[opt.id], filterParams));
    },
  };
};

interface IBuildDynamicDropdownOptionMapProps {
  ironHeadModel: APIModel.HeadModelForDropdownDto[];
}

export const buildDynamicDropdownOptionMap = ({ ironHeadModel }: IBuildDynamicDropdownOptionMapProps) => {
  return {
    ironHeadModelDropdownOptionMap: buildDynamicDropdown(ironHeadModel, 'headModelId', 'name'),
  };
};
